import React, {useMemo, useState} from 'react';
import {Switch} from 'antd';
import dayjs from 'dayjs';
import TablePage from "../../../pages/tablePage/TablePage";
import IsLoading from "../../../shared/components/IsLoading";
import ErrorPage from "../../../shared/components/ErrorPage";
import {useTrophyChallenge, useTrophyChallengeGroup, useChangeTrophyChallengeOnOff, useTrophyChallengeMissionGroup} from "./useTrophyChallenge";
import TrophyChallengeEditModal from "./TrophyChallengeEditModal";
import {TrophyChallengeEntity} from "./TrophyChallengeEntity";
import toast from "react-hot-toast";
import {REFRESH_TYPE_FUNCTION} from "../../../shared/utils/enum";
import {useReloadTime} from "../../serverInfo/useServerInfo";

const TrophyChallenge = () => {
    const [rowData, setRowData] = useState(null);
    const {data, isLoading, error} = useTrophyChallenge();
    const {data: groupRows, isLoading: isGroupLoading, error: groupError} = useTrophyChallengeGroup();
    const {data: missionGroupRows, isLoading: isMissionGroupLoading, error: missionGroupError} = useTrophyChallengeMissionGroup();
    const {data: reloadTimeData, isLoading: isReloadTimeData, error: reloadTimeDataError, refetch: reloadTimeDataRefetch} = useReloadTime();
    const {mutate: changeOnOff} = useChangeTrophyChallengeOnOff();

    // trophy_challenge_group.seq → group 맵
    const groupMap = useMemo(() => {
        const map = {};
        (groupRows || []).forEach(g => { map[g.seq] = g; });
        return map;
    }, [groupRows]);

    // trophy_challenge_mission_group.groupIdx → 대표 그룹 정보 맵
    const missionGroupMap = useMemo(() => {
        const map = {};
        (missionGroupRows || []).forEach(row => {
            if (!map[row.groupIdx]) {
                map[row.groupIdx] = {groupIdx: row.groupIdx, memo: row.memo};
            }
        });
        return map;
    }, [missionGroupRows]);

    const missionGroupOptions = useMemo(() => Object.values(missionGroupMap), [missionGroupMap]);

    const columns = useMemo(() => ([
        {
            title: '시퀀스',
            dataIndex: 'trophyId',
            key: 'trophyId',
            width: 80,
            sorter: (a, b) => a.trophyId - b.trophyId,
        },
        {
            title: '메모',
            dataIndex: 'memo',
            key: 'memo',
        },
        {
            title: '계정 레벨',
            dataIndex: 'openLevel',
            key: 'openLevel',
            width: 100,
        },
        {
            title: '챌린지 그룹',
            dataIndex: 'challengeGroupIdx',
            key: 'challengeGroupIdx',
            width: 160,
            render: v => {
                const g = groupMap[v];
                return g ? `${v}${g.memo ? `(${g.memo})` : ''}` : v || '-';
            },
        },
        {
            title: '챌린지 미션',
            dataIndex: 'missionGroupIdx',
            key: 'missionGroupIdx',
            width: 200,
            render: v => {
                const m = missionGroupMap[v];
                return m ? `${v}${m.memo ? `(${m.memo})` : ''}` : v || '-';
            },
        },
        {
            title: '완료 조건(트로피 개수)',
            dataIndex: 'clearCount',
            key: 'clearCount',
            width: 160,
        },
        {
            title: 'On/Off',
            dataIndex: 'onOff',
            key: 'onOff',
            width: 90,
            filters: [{text: 'On', value: 1}, {text: 'Off', value: 0}],
            onFilter: (value, record) => record.onOff === value,
            render: (val, record) => (
                <div onClick={e => e.stopPropagation()}>
                    <Switch
                        checked={val === 1}
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        onChange={checked => {
                            changeOnOff(
                                {trophyId: record.trophyId, onOff: checked ? 1 : 0},
                                {onError: () => toast.error('On/Off 변경 실패')}
                            );
                        }}
                    />
                </div>
            ),
        },
        {
            title: 'Start',
            dataIndex: 'begin',
            key: 'begin',
            width: 170,
            render: v => dayjs.unix(v).format('YYYY-MM-DD HH:mm:ss'),
        },
        {
            title: 'End',
            dataIndex: 'end',
            key: 'end',
            width: 170,
            render: v => dayjs.unix(v).format('YYYY-MM-DD HH:mm:ss'),
        },
        {
            title: '최소 적용 버전',
            key: 'minVersion',
            width: 130,
            render: (_, record) => groupMap[record.challengeGroupIdx]?.minVersion || '-',
        },
    ]), [groupMap, missionGroupMap, changeOnOff]);

    if (isLoading || isGroupLoading || isMissionGroupLoading || isReloadTimeData) return <IsLoading/>;
    const fetchError = error || groupError || missionGroupError || reloadTimeDataError;
    if (fetchError) return <ErrorPage error={fetchError}/>;

    const props = {
        columns,
        localData: data || [],
        modal: TrophyChallengeEditModal,
        buttonText: '신규 챌린지 추가',
        rowData,
        setRowData,
        entityModel: TrophyChallengeEntity,
        modalOptions: {
            groupRows: groupRows || [],
            missionGroupOptions,
        },
        modalWidth: 700,
        modalResizable: true,
        refreshTypeFunc: REFRESH_TYPE_FUNCTION.GAME_DATA,
        reloadTime: reloadTimeData?.game_data_sync_id,
        reloadTimeRefetch: reloadTimeDataRefetch
    };

    return <TablePage {...props}/>;
};

export default TrophyChallenge;
